/**
 * Cookie Consent — entry module.
 *
 * Bootstraps the ConsentStore, banner + customize UI controllers, and
 * script loader, then exposes window.jkd_consent as the public API.
 *
 * Reads server-rendered config from window.jkdccConfig (printed by
 * jkd-cookie-consent/includes/config.php via wp_localize_script).
 *
 * Entry for the standalone JKD Cookie Consent plugin bundle. It imports its
 * own styles so the CSS ships in the same bundle, enqueued only when the
 * integration is enabled.
 */

import '../scss/cookie-consent.scss';
import ConsentStore from './consent-store.js';
import BannerController from './banner.js';
import CustomizeController from './customize.js';
import ScriptLoader from './script-loader.js';

const BOTTOM_SURFACE_CLAIM_EVENT = 'jkd:bottom-surface:claim';
const BOTTOM_SURFACE_RELEASE_EVENT = 'jkd:bottom-surface:release';

function boot() {
	const config = window.jkdccConfig;
	if (!config || !config.enabled) return;

	const root = document.querySelector('[data-consent-root]');
	if (!root) return;

	// Editor-configurable bar width (Cookie Consent integration option).
	if (config.bannerWidth) {
		root.style.setProperty('--jkdcc-banner-w', `${config.bannerWidth}px`);
	}

	const store = new ConsentStore({ expirationMonths: config.expirationMonths });
	const customize = new CustomizeController({ root, store, copy: config.copy });
	const banner = new BannerController({
		root,
		store,
		copy: config.copy,
		devMode: !!config.devMode,
		onOpenCustomize: () => customize.open(),
	});
	const loader = new ScriptLoader(store);

	customize.init();
	banner.init();
	loader.init();

	document.addEventListener(BOTTOM_SURFACE_CLAIM_EVENT, (event) => {
		banner.suspend(String(event.detail?.owner || 'anonymous'));
	});
	document.addEventListener(BOTTOM_SURFACE_RELEASE_EVENT, (event) => {
		banner.resume(String(event.detail?.owner || 'anonymous'));
	});
	const activeBottomOwner = document.documentElement.getAttribute('data-jkd-bottom-surface-owner');
	if (activeBottomOwner) banner.suspend(activeBottomOwner);

	// Public API
	window.jkd_consent = {
		has: (cat) => store.has(cat),
		on: (event, handler) => store.on(event, handler),
		grant: (cats) => store.grant(cats),
		revoke: (cats) => store.revoke(cats),
		clear: () => store.clear(),
		open: () => customize.open(),
		close: () => customize.close(),
		// Whether the consent banner is currently occupying the bottom-bar slot.
		// A feature that shares the slot (Reading Bar) reads this at boot and
		// otherwise waits for the `jkd:consent:dismissed` DOM event.
		isBannerVisible: () => banner.isVisible(),
	};

	document.addEventListener('click', (e) => {
		if (e.target.closest('[data-consent-reopen]')) {
			e.preventDefault();
			customize.open();
		}
	});
}

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', boot);
} else {
	boot();
}
